import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  List,
  ListItem,
  Paper,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Chip,
  Card,
  CardContent,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Tooltip,
  Menu,
  MenuItem,
  Drawer,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  Person,
  Science,
  LocalHospital,
  Chat,
  Event,
  CalendarToday,
  Support,
  Analytics,
  Warning,
  Mail,
  SmartToy,
  Close,
  PersonAdd,
  AddCircle,
  DragIndicator,
  Circle,
  ChevronRight,
  LocationOn
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import MammoAssistChat from '../components/MammoAssistChat';

// Remove fixed width - using Grid system now for responsive layout

const LeftPanel = ({ open, onClose, isMobile, onOpenChat }) => {
  const { user, currentPortal } = useAuth();
  const navigate = useNavigate();
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [quickAccessTab, setQuickAccessTab] = useState(0);
  const [connectionStatus, setConnectionStatus] = useState({
    fastapi: 'disconnected',
    mcp: 'disconnected'
  });
  const [addUserMenuAnchor, setAddUserMenuAnchor] = useState(null);
  const [addAgentMenuAnchor, setAddAgentMenuAnchor] = useState(null);
  const displayName = [user?.givenName || user?.first_name, user?.familyName || user?.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
    || (user?.email ? user.email.split('@')[0] : '')
    || 'Patient';

  // Monitor connection status to FastAPI and MCP server
  useEffect(() => {
    const checkConnections = async () => {
      try {
        // Check FastAPI connection
        await fetch('http://localhost:8007/api/health', {
          method: 'GET',
          mode: 'no-cors',
          signal: AbortSignal.timeout(5000)
        });
        setConnectionStatus(prev => ({
          ...prev,
          fastapi: 'connected'
        }));
      } catch (error) {
        setConnectionStatus(prev => ({
          ...prev,
          fastapi: 'disconnected'
        }));
      }

      try {
        // Check MCP server connection (assuming it runs on port 3001)
        await fetch('http://localhost:3001/health', {
          method: 'GET',
          mode: 'no-cors',
          signal: AbortSignal.timeout(5000)
        });
        setConnectionStatus(prev => ({
          ...prev,
          mcp: 'connected'
        }));
      } catch (error) {
        setConnectionStatus(prev => ({
          ...prev,
          mcp: 'disconnected'
        }));
      }
    };

    // Check connections on mount and every 30 seconds
    checkConnections();
    const interval = setInterval(checkConnections, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const handleAddUser = (userType) => {
    // TODO: Implement user invitation logic
    setAddUserMenuAnchor(null);
  };

  const handleAddAgent = (agentType) => {
    // TODO: Implement agent addition logic
    setAddAgentMenuAnchor(null);
  };

  const getConnectionStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'success.main';
      case 'error': return 'error.main';
      default: return 'grey.400';
    }
  };

  const getPortalTitle = () => {
    switch (currentPortal) {
      case 'patient': return 'Patient Portal';
      case 'trials': return 'Clinical Trials Portal';
      case 'community': return 'Community Portal';
      default: return 'MammoTrials Portal';
    }
  };

  const quickAccessTabs = [
    {
      id: 'events',
      label: 'Events',
      route: '/patient-events',
      icon: <CalendarToday fontSize="small" />,
      items: [
        { icon: '📅', title: 'Community Health Fair - First Coast', subtitle: '2025-09-16 at 10:00 AM' },
        { icon: '📝', title: 'Health Planning Workshop', subtitle: '2025-09-05 at 2:00 PM' },
        { icon: '🤝', title: 'Community Engagement Meeting', subtitle: '2025-09-22 at 11:00 AM' }
      ]
    },
    {
      id: 'alerts',
      label: 'Alerts',
      route: '/patient-alerts',
      badge: 2,
      icon: (
        <Badge badgeContent={2} color="error">
          <Warning fontSize="small" />
        </Badge>
      ),
      items: [
        { icon: '⚠️', title: 'Baseline lab deadline approaching', subtitle: 'Due in 2 days' },
        { icon: '🚗', title: 'Transportation confirmation needed', subtitle: 'Action requested today' }
      ]
    },
    {
      id: 'invites',
      label: 'Invites',
      route: '/patient-invites',
      badge: 1,
      icon: (
        <Badge badgeContent={1} color="primary">
          <Mail fontSize="small" />
        </Badge>
      ),
      items: [
        { icon: '✉️', title: 'Invite to trial orientation session', subtitle: 'Hosted by Clinical Trial Management Team' }
      ]
    },
  ];

  const activeQuickAccess = quickAccessTabs[quickAccessTab];

  const QuickAccessCard = () => {
    if (currentPortal !== 'patient') {
      return null;
    }

    return (
      <Paper elevation={1} sx={{ mx: 2, mb: 2, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
            Todo & Quick Access
          </Typography>

          <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, mb: 2, bgcolor: 'grey.50' }}>
            <Typography variant="caption" color="text.secondary">User Context:</Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              {displayName} - <Box component="span" sx={{ fontWeight: 700 }}>{user?.primary_role?.replace('_', ' ').toUpperCase() || 'PATIENT'}</Box>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {user?.county || 'Unknown County'}
            </Typography>
          </Paper>

          <Tabs
            value={quickAccessTab}
            onChange={(_, value) => setQuickAccessTab(value)}
            variant="fullWidth"
            sx={{ minHeight: 48, mb: 1.5 }}
          >
            {quickAccessTabs.map((tab) => (
              <Tab
                key={tab.id}
                icon={tab.icon}
                iconPosition="start"
                label={tab.label}
                sx={{ minHeight: 48 }}
              />
            ))}
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {displayName}'s {activeQuickAccess.label}
            </Typography>
            <Button size="small" startIcon={<LocationOn fontSize="small" />} onClick={() => navigate(activeQuickAccess.route)}>
              Open
            </Button>
          </Box>

          <List dense sx={{ py: 0 }}>
            {activeQuickAccess.items.map((item) => (
              <ListItemButton
                key={item.title}
                sx={{ px: 0, borderRadius: 1.5 }}
                onClick={() => navigate(activeQuickAccess.route, { state: { selectedItem: item } })}
              >
                <ListItemIcon sx={{ minWidth: 28 }}>
                  <Typography variant="body2">{item.icon}</Typography>
                </ListItemIcon>
                <ListItemText
                  primary={item.title}
                  secondary={item.subtitle}
                  primaryTypographyProps={{ variant: 'caption', sx: { fontWeight: 600 } }}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
                <ChevronRight fontSize="small" color="action" />
              </ListItemButton>
            ))}
          </List>

          <Button
            fullWidth
            sx={{ mt: 1 }}
            onClick={() => navigate(activeQuickAccess.route)}
            endIcon={<ChevronRight fontSize="small" />}
          >
            View All Items
          </Button>
        </Box>
      </Paper>
    );
  };

  const MammoAssistPanel = () => (
    <Card sx={{ mx: 2, mb: 2, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 }, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            mr: 1
          }}>
            <Typography variant="h6" color="white" sx={{ fontWeight: 'bold' }}>
              M
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            MammoAssist™
          </Typography>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Chat with,
            </Typography>
            <Chip 
              label={user?.primary_role?.replace('_', ' ') || 'User'} 
              size="small" 
              variant="filled"
              sx={{ textTransform: 'capitalize' }}
            />
          </Box>
          
          <List dense sx={{ py: 0 }}>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <Typography>🔬</Typography>
              </ListItemIcon>
              <ListItemText 
                primary="Clinical trial matching with mCODE data" 
                primaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <Typography>🤖</Typography>
              </ListItemIcon>
              <ListItemText 
                primary="AI-powered eligibility assessment" 
                primaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ px: 0, py: 0.5 }}>
              <ListItemIcon sx={{ minWidth: 24 }}>
                <Typography>💗</Typography>
              </ListItemIcon>
              <ListItemText 
                primary="Personalized breast cancer trial recommendations" 
                primaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>
          
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<Chat />}
            title="Open MammoAssist chat"
            aria-label="Open MammoAssist AI chat assistant"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (typeof onOpenChat === 'function') {
                onOpenChat();
              } else {
                setChatDialogOpen(true);
              }
            }}
            sx={{ mt: 1 }}
          >
            Chat with MammoAssist™!
          </Button>
        </Box>
        
        <Typography 
          variant="body2" 
          color="text.secondary"
          sx={{ textAlign: 'center', mt: 'auto' }}
        >
          Ready to assist {user?.first_name || 'User'}!
        </Typography>
      </CardContent>
    </Card>
  );

  const UserProfileCard = () => (
    <Card sx={{ mx: 2, mt: 2, mb: 2 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            sx={{ 
              width: 48, 
              height: 48, 
              bgcolor: 'primary.main',
              fontSize: '1.2rem',
              fontWeight: 'bold'
            }}
          >
            {user?.first_name?.[0]}{user?.last_name?.[0]}
          </Avatar>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                color: 'text.primary',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user?.first_name} {user?.last_name}
            </Typography>
            
            <Typography 
              variant="caption" 
              color="text.secondary"
              sx={{ 
                display: 'block',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user?.email}
            </Typography>
            
            <Chip
              label={user?.primary_role?.replace('_', ' ').toUpperCase() || 'USER'}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ 
                mt: 0.5,
                height: 18,
                fontSize: '0.65rem',
                textTransform: 'capitalize'
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box 
              sx={{ 
                width: 8, 
                height: 8, 
                bgcolor: 'success.main', 
                borderRadius: '50%',
                mb: 0.5
              }} 
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem' }}>
              Online
            </Typography>
          </Box>
        </Box>
        
        {/* Portal Context Indicator */}
        <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="caption" color="text.secondary">
              Current Portal
            </Typography>
            <Chip
              label={getPortalTitle()}
              size="small"
              color="secondary"
              sx={{ 
                height: 20,
                fontSize: '0.7rem'
              }}
            />
          </Box>
          {currentPortal === 'patient' && (
            <Button
              fullWidth
              size="small"
              variant="outlined"
              sx={{ mt: 1 }}
              onClick={() => navigate('/clinical-profile')}
            >
              Edit Profile
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  const panelContent = (
    <Box sx={{ 
      width: '100%', 
      height: '100vh', 
      bgcolor: 'background.paper', 
      display: 'flex', 
      flexDirection: 'column',
      borderRight: 1,
      borderColor: 'divider'
    }}>
      {/* User Profile Card */}
      <UserProfileCard />

      {/* Patient Quick Access */}
      <QuickAccessCard />

    </Box>
  );

  // MammoAssist Chat Dialog
  const MammoAssistDialog = () => {
    return (
    <Dialog
      open={chatDialogOpen}
      onClose={() => {
        setChatDialogOpen(false);
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { 
          height: '85vh', 
          maxHeight: '700px',
          cursor: 'move' // Indicate draggable
        },
        // Make dialog draggable
        onMouseDown: (e) => {
          const dialog = e.currentTarget;
          const rect = dialog.getBoundingClientRect();
          const offsetX = e.clientX - rect.left;
          const offsetY = e.clientY - rect.top;

          const handleMouseMove = (e) => {
            dialog.style.left = `${e.clientX - offsetX}px`;
            dialog.style.top = `${e.clientY - offsetY}px`;
            dialog.style.position = 'fixed';
            dialog.style.margin = '0';
          };

          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        pb: 1,
        borderBottom: 1,
        borderColor: 'divider',
        cursor: 'move'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DragIndicator sx={{ color: 'text.secondary' }} />
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            <SmartToy />
          </Avatar>
          <Box>
            <Typography variant="h6">
              MammoAssist™ AI Assistant
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Personalized breast health support for {user?.first_name || 'you'}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Connection Status */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title={`FastAPI: ${connectionStatus.fastapi}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Circle sx={{ 
                  fontSize: 8, 
                  color: getConnectionStatusColor(connectionStatus.fastapi) 
                }} />
                <Typography variant="caption" color="text.secondary">
                  API
                </Typography>
              </Box>
            </Tooltip>
            
            <Tooltip title={`MCP Server: ${connectionStatus.mcp}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Circle sx={{ 
                  fontSize: 8, 
                  color: getConnectionStatusColor(connectionStatus.mcp) 
                }} />
                <Typography variant="caption" color="text.secondary">
                  MCP
                </Typography>
              </Box>
            </Tooltip>
          </Box>
          
          {/* Add User Button */}
          <Tooltip title="Invite user to chat">
            <IconButton 
              size="small"
              onClick={(e) => setAddUserMenuAnchor(e.currentTarget)}
              aria-label="Invite user to chat"
            >
              <PersonAdd />
            </IconButton>
          </Tooltip>
          
          {/* Add Agent Button */}
          <Tooltip title="Add AI agent to chat">
            <IconButton 
              size="small"
              onClick={(e) => setAddAgentMenuAnchor(e.currentTarget)}
              aria-label="Add AI agent to chat"
            >
              <AddCircle />
            </IconButton>
          </Tooltip>
          
          {/* Close Button */}
          <IconButton 
            onClick={() => setChatDialogOpen(false)}
            size="small"
            title="Close dialog"
            aria-label="Close MammoAssist chat dialog"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        <MammoAssistChat 
          userRole={user?.primary_role}
          portalContext={currentPortal}
          compact={false}
          connectionStatus={connectionStatus}
        />
      </DialogContent>

      {/* Add User Menu */}
      <Menu
        anchorEl={addUserMenuAnchor}
        open={Boolean(addUserMenuAnchor)}
        onClose={() => setAddUserMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleAddUser('doctor')}>
          <ListItemIcon><LocalHospital fontSize="small" /></ListItemIcon>
          <ListItemText primary="Invite Doctor" />
        </MenuItem>
        <MenuItem onClick={() => handleAddUser('nurse')}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText primary="Invite Nurse" />
        </MenuItem>
        <MenuItem onClick={() => handleAddUser('patient')}>
          <ListItemIcon><Person fontSize="small" /></ListItemIcon>
          <ListItemText primary="Invite Patient" />
        </MenuItem>
        <MenuItem onClick={() => handleAddUser('caregiver')}>
          <ListItemIcon><Support fontSize="small" /></ListItemIcon>
          <ListItemText primary="Invite Caregiver" />
        </MenuItem>
      </Menu>

      {/* Add Agent Menu */}
      <Menu
        anchorEl={addAgentMenuAnchor}
        open={Boolean(addAgentMenuAnchor)}
        onClose={() => setAddAgentMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleAddAgent('clinical')}>
          <ListItemIcon><Science fontSize="small" /></ListItemIcon>
          <ListItemText primary="Clinical Research Agent" />
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('diagnostic')}>
          <ListItemIcon><Analytics fontSize="small" /></ListItemIcon>
          <ListItemText primary="Diagnostic Agent" />
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('support')}>
          <ListItemIcon><Support fontSize="small" /></ListItemIcon>
          <ListItemText primary="Patient Support Agent" />
        </MenuItem>
        <MenuItem onClick={() => handleAddAgent('scheduling')}>
          <ListItemIcon><Event fontSize="small" /></ListItemIcon>
          <ListItemText primary="Scheduling Agent" />
        </MenuItem>
      </Menu>
    </Dialog>
    );
  };

  // For mobile, use a temporary drawer overlay
  if (isMobile) {
    return (
      <>
        <Drawer
          variant="temporary"
          open={open}
          onClose={onClose}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: '80vw', // Use viewport width for responsive mobile
            },
          }}
        >
          {panelContent}
        </Drawer>
      </>
    );
  }

  // For desktop, render directly in the grid (no drawer overlay)
  return (
    <>
      {panelContent}
    </>
  );
};

export default LeftPanel;